import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { RaceEventIconLayout } from '../../../../layouts/raceEvent/RaceEventIconLayout';
import { attachIntervalToDisplayObject, CountDownFormat, formatIntoUnitCountdown } from '../../../utils/time-utils';
import { getRaceEventEndTime, getRaceEventMinLevel } from '../RaceEventUtils';
import type { IObservableValue, MenuIcon } from '../../../mainMenu/types';
import type { RaceEventState } from '../types';
import { LevelLockMenuIconOverlay } from '../../../views/LevelLockMenuIconOverlay';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { RaceEventIconRankFlagView } from './RaceEventIconRankFlagView';
import { preloadLayoutsAssets } from '../../../views/preloadLayoutsAssets';
import { RaceEventPopup } from './RaceEventPopup';
import { RaceEventCarLayout } from '../../../../layouts/raceEvent/RaceEventCarLayout';

export interface RaceEventIconViewConfig {
  state: IObservableValue<{
    levelIndex: number;
    raceEvent: RaceEventState | null;
  }>;
}

export class RaceEventIconView extends Container implements MenuIcon {
  public readonly onVisibilityChanged = new Signal<(visible: boolean) => void>();

  public readonly onClicked = new Signal();

  private layout: RaceEventIconLayout | null = null;

  private cancelTimeUpdate: () => void = () => {};

  public isReady = false;

  constructor(private readonly config: RaceEventIconViewConfig) {
    super();

    preloadLayoutsAssets([RaceEventIconLayout, RaceEventPopup, RaceEventCarLayout]).then(
      () => {
        this.initLayout();
        this.isReady = true;
      },
      () => {},
    );
  }

  private initLayout(): void {
    if (this.destroyed) return;

    this.layout = new RaceEventIconLayout();

    void playLayoutAnimation(this.layout.carLayout, 'Car_Idle', true);
    void playLayoutAnimation(this.layout.carLayout, 'Tires_Idle', true);

    if (getRaceEventMinLevel() > this.config.state.getValue().levelIndex + 1) {
      this.initLocked();
    } else {
      this.init(this.config);
    }
  }

  private initLocked(): void {
    if (!this.layout) return;
    const { root, timerContainer, joinButton, positionContainer } = this.layout;

    root.props({
      parent: this,
    });

    timerContainer.visible = false;
    joinButton.visible = false;
    positionContainer.visible = false;

    new LevelLockMenuIconOverlay(getRaceEventMinLevel()).props({
      parent: this,
    });
  }

  private init(config: RaceEventIconViewConfig): void {
    if (!this.layout) return;
    const { root, positionContainer } = this.layout;

    root.props({ parent: this });

    root.on('pointertap', () => {
      this.onClicked.emit();
    });

    config.state.addChangeHandler(this, () => {
      this.refreshState();
    });

    positionContainer.tweenTo(
      {
        pivotY: [3, 0],
      },
      1000,
      'sinOut',
      true,
      0,
      -1,
    );
  }

  private refreshState(): void {
    if (!this.layout) return;
    const { timerContainer, timerText, joinButton, positionContainer } = this.layout;
    const { raceEvent } = this.config.state.getValue();

    this.cancelTimeUpdate();

    this.visible = !!raceEvent;
    this.onVisibilityChanged.emit(this.visible);

    if (!raceEvent) return;

    joinButton.visible = raceEvent.startDate === null;
    positionContainer.visible = raceEvent.startDate !== null;
    timerContainer.visible = raceEvent.startDate !== null;

    if (raceEvent.startDate === null) return;

    this.cancelTimeUpdate = attachIntervalToDisplayObject(
      timerText,
      () => {
        const timeLeft = getRaceEventEndTime(raceEvent) - Date.now();
        timerText.text = formatIntoUnitCountdown(timeLeft, 2, CountDownFormat.DHM);
        if (timeLeft <= 0) timerContainer.visible = false;
      },
      1000,
    );
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public displayFlag(rank: number): void {
    if (!this.layout) {
      return;
    }
    new RaceEventIconRankFlagView(rank).props({
      parent: this.layout.positionContainer,
    });
    this.layout.positionContainer.visible = true;
  }

  public async acceptFlagContainer(flagContainer: Container): Promise<void> {
    if (!this.layout) return;

    this.layout.positionContainer.visible = true;

    if (!flagContainer.parent) return;

    const targetPosition = flagContainer.parent.toLocal(this.layout.positionContainer.getGlobalPosition());

    await flagContainer
      .tweenTo(
        {
          x: targetPosition.x,
          y: targetPosition.y,
          scaleXY: 0.2,
        },
        600,
        'cubicInOut',
        true,
      )
      .asyncOnComplete();

    flagContainer.props({
      parent: this.layout.positionContainer,
      x: 0,
      y: 0,
    });
  }
}
